import { useEffect, useRef, useState } from "react";
import { QuestionAndAnswer } from "../components/interview/QuestionAndAnswer";
import { HoldToRecord } from "../components/interview/HoldToRecord";
import { TypeAnswer } from "../components/interview/TypeAnswer";
import { ProgressDots } from "../components/interview/ProgressDots";
import { HistoryDrawer, type HistoryEntry } from "../components/interview/HistoryDrawer";
import { useQuestionVoice } from "../hooks/useQuestionVoice";

const SAMPLE_QUESTIONS = [
  "What was the first thing you did this morning before this call?",
  "Walk me through a typical week — Monday looks different from Friday, how?",
  "What's the part of running this that you've stopped trying to fix?",
  "What's the bet you're making with this business?",
  "What do you know now that you couldn't have taught yourself five years ago?",
  "What did I not ask that I should have?",
];

const SAMPLE_ANSWERS = [
  "Opened up the shop, made coffee, glanced at the inbox and decided not to read it yet.",
  "Mondays are intake — every customer is dropping something off. Friday is closing tickets.",
  "Getting back to people the same day. We just can't, anymore.",
  "That a small place can feel personal in a way the big places can't.",
  "Which problems are actually worth solving versus which ones I just don't like.",
  "Whether I'd do it again knowing what I know now. Yeah. I would.",
];

const ANSWER_HOLD_MS = 2400;

type Phase =
  | "thinking"
  | "streaming"
  | "reading_question"
  | "showing_answer"
  | "terminated";

export default function PreviewInterview() {
  const [questionIdx, setQuestionIdx] = useState(0);
  const [phase, setPhase] = useState<Phase>("thinking");
  const [streamingText, setStreamingText] = useState("");
  const [lastAnswer, setLastAnswer] = useState<{ text: string; typed: boolean } | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [draft, setDraft] = useState("");
  const [streamingMode, setStreamingMode] = useState<"simulated" | "instant">("simulated");

  // Track whether the current draft came from voice. Flip to false when the
  // user types fresh after a clean slate. Used to label the echo "you said" vs "you wrote".
  const draftFromVoice = useRef(false);
  const onDraftChange = (next: string) => {
    if (next.trim().length === 0) draftFromVoice.current = false;
    setDraft(next);
  };

  // Preview uses the public ElevenLabs preview endpoint — real production voice.
  const voice = useQuestionVoice("preview");

  useEffect(() => {
    if (phase !== "thinking") return;
    const fullText = SAMPLE_QUESTIONS[questionIdx];

    if (streamingMode === "instant") {
      const t = window.setTimeout(() => {
        setStreamingText("");
        setPhase("reading_question");
        void voice.speak(fullText);
      }, 400);
      return () => window.clearTimeout(t);
    }

    const start = window.setTimeout(() => {
      setStreamingText("");
      setPhase("streaming");
      const words = fullText.split(" ");
      let acc = "";
      let i = 0;
      const tick = () => {
        if (i >= words.length) {
          setPhase("reading_question");
          void voice.speak(fullText);
          return;
        }
        acc = i === 0 ? words[i] : `${acc} ${words[i]}`;
        setStreamingText(acc);
        i += 1;
        window.setTimeout(tick, 90 + Math.random() * 90);
      };
      tick();
    }, 600);
    return () => window.clearTimeout(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, questionIdx, streamingMode]);

  const handleAnswer = (text: string, typed: boolean) => {
    voice.stop();
    const q = SAMPLE_QUESTIONS[questionIdx];
    setHistory((prev) => [...prev, { question: q, answer: text, typed }]);
    setLastAnswer({ text, typed });
    setDraft("");
    setPhase("showing_answer");
    window.setTimeout(() => {
      setLastAnswer(null);
      if (questionIdx >= SAMPLE_QUESTIONS.length - 1) {
        setPhase("terminated");
        return;
      }
      setQuestionIdx((i) => i + 1);
      setStreamingText("");
      setPhase("thinking");
    }, ANSWER_HOLD_MS);
  };

  // Simulate transcription landing: push sample answer into the draft so the
  // user can edit and send (matches the unified speak-fills-the-type-box flow).
  const simulateTranscript = () => {
    const transcript = SAMPLE_ANSWERS[questionIdx] ?? "Whatever was on my mind.";
    setDraft((prev) => (prev.trim().length > 0 ? `${prev} ${transcript}` : transcript));
    draftFromVoice.current = true;
  };

  if (phase === "terminated") {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center px-6 py-12 text-center">
        <div className="max-w-xl space-y-7">
          <h1 className="font-hand text-6xl text-ink-900">Thank you.</h1>
          <p className="text-ink-700 leading-relaxed text-lg">
            You called it when it was right. Your transcript is saved.
          </p>
          <p className="text-xs text-ink-300 uppercase tracking-[0.2em]">You can close this window</p>
          <button
            onClick={() => {
              setQuestionIdx(0);
              setStreamingText("");
              setHistory([]);
              setPhase("thinking");
            }}
            className="text-xs uppercase tracking-[0.22em] text-ink-300 hover:text-ink-500 transition-colors"
          >
            Restart preview
          </button>
        </div>
      </main>
    );
  }

  const thinking = phase === "thinking";
  const canAnswer = phase === "reading_question";
  const showingAnswer = phase === "showing_answer";
  const currentText =
    phase === "reading_question" || phase === "showing_answer"
      ? SAMPLE_QUESTIONS[questionIdx]
      : null;

  return (
    <main className="min-h-screen flex flex-col px-6 py-7 sm:py-10">
      {/* Tiny dev controls */}
      <div className="absolute top-4 right-4 flex items-center gap-4 text-[10px] uppercase tracking-[0.22em] text-ink-300">
        <button
          onClick={() => setStreamingMode((m) => (m === "simulated" ? "instant" : "simulated"))}
          className="hover:text-ink-500 transition-colors"
        >
          {streamingMode === "simulated" ? "Streaming" : "Instant"}
        </button>
      </div>

      <header className="flex items-center justify-between max-w-3xl w-full mx-auto">
        <div className="flex items-center gap-5">
          <ProgressDots total={SAMPLE_QUESTIONS.length} current={questionIdx} />
          <HistoryDrawer entries={history} />
        </div>
        <button
          type="button"
          onClick={() => setPhase("terminated")}
          className="text-[10px] uppercase tracking-[0.22em] text-ink-300 hover:text-ink-500 transition-colors"
        >
          I'm done
        </button>
      </header>

      <section className="flex-1 flex flex-col items-center justify-center max-w-3xl w-full mx-auto py-8 sm:py-16">
        <QuestionAndAnswer
          questionText={currentText}
          streamingQuestion={streamingText}
          answerText={lastAnswer?.text ?? null}
          questionLive={phase === "streaming"}
          showingAnswer={showingAnswer}
          answerLabel={lastAnswer?.typed ? "you wrote" : "you said"}
          onReplay={voice.replay}
          isSpeaking={voice.isSpeaking}
        />
        {thinking && (
          <p className="mt-8 text-[10px] uppercase tracking-[0.22em] text-ink-300">One moment</p>
        )}
      </section>

      <footer className="max-w-3xl w-full mx-auto flex flex-col items-center gap-10 pb-6 sm:pb-10 min-h-[18rem]">
        {canAnswer ? (
          <>
            <HoldToRecord onSubmit={() => simulateTranscript()} onError={() => void 0} />
            <TypeAnswer
              value={draft}
              onValueChange={onDraftChange}
              onSubmit={(text) => handleAnswer(text, !draftFromVoice.current)}
              placeholder="Press the mic, or write what comes to mind…"
              autoFocus={false}
            />
          </>
        ) : (
          <div className="h-40" aria-hidden="true" />
        )}
      </footer>
    </main>
  );
}
